import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

import { mcmEndpointToName } from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { Plan, VenueDisposition } from '@go-shared/types/plan';
import {
  CommonTextTranslation,
  ContentTemplateTranslation,
  EventTemplateTranslation,
  VenueTemplateTranslation,
} from '@go-shared/types/translation';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { BubbleButton, BubbleCheckbox, BubbleFormGroup, BubbleInput } from '../BubbleUI';

import './DailySchedulePanel.scss';
import { BareEvent, Event } from '@go-shared/types/event';
import DailyEventListPanel from './DailyEventListPanel';
import DailyContentListPanel from './DailyContentListPanel';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { canEditCanvas } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { isDayLocked } from '@go-client/lib/sailing';
import { SailingDayBundle } from '@go-shared/types/sailing';
import { extractTemplateStatus } from '@go-client/lib/itinerary';
import { Content } from '@go-shared/types/content';
import { GroupEvent } from '@go-shared/types/groupEvent';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import DailyVenueListPanel from './DailyVenueListPanel';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getHourFormat } from '@go-shared/lib/property';
import { BubbleTableFieldBehavior, BubbleTableFields } from '../BubbleUI/BubbleTable';
import { notUndefined } from '@go-shared/lib/functional';
import { ReserveEvent } from '@go-shared/types/reserve';

export type DailySchedulePanelMode = 'default' | 'mcm' | 'focus';

interface DailySchedulePanelPropsBase {
  open: boolean;
  width: number;
  plan: Plan;
  groupEvts: GroupEvent[];
  reserveEvts: ReserveEvent[];
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  contentTemplates: BareContentTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  contentTemplateTranslations: ContentTemplateTranslation[];
  venueTemplateTranslations: VenueTemplateTranslation[];
  commonTextTranslations: CommonTextTranslation[];
  dayBundle?: SailingDayBundle;
  onToggle: (show: boolean) => void;
  onAddBtnClick: (activeTab: SchedulePanelTabs) => void;
  onUpdateEvent: (event: Event, applyStaffToPlan?: boolean, transferToTargetDay?: string) => void;
  onEditEvent: (event: Event) => void;
  onRemoveEvent: (eventId: string) => void;
  onCreateEvent: (event: Event) => Promise<BareEvent>;
  onUpdateContent: (content: Content, transferToTargetDay?: string) => void;
  onEditContent: (content: Content) => void;
  onRemoveContent: (contentId: string) => void;
  onUpdateVenue: (venue: VenueDisposition) => void;
}

type DailySchedulePanelProps = DailySchedulePanelPropsBase & React.HTMLProps<HTMLDivElement>;

type SchedulePanelTabs = 'events' | 'venueData' | 'content';

export interface DailySchedulePanelRef {
  toggle(tab: SchedulePanelTabs): void;
}

export function GENERATE_TABLE_FIELDS<T extends {}>(opts?: { skipHints?: boolean }) {
  const result: Array<BubbleTableFields<T>> = [
    {
      key: 'time',
      behavior: BubbleTableFieldBehavior.MinWidth,
    },
    !opts?.skipHints
      ? {
          key: 'hints',
          behavior: BubbleTableFieldBehavior.Condense,
        }
      : undefined,
    {
      key: 'title',
    },
    {
      key: 'actions-1',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'actions-2',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'minimizer',
      behavior: BubbleTableFieldBehavior.Collapse,
    },
  ].filter(notUndefined);

  return result;
}

export default forwardRef<DailySchedulePanelRef, DailySchedulePanelProps>((props, ref) => {
  const {
    open,
    width,
    onToggle,
    plan,
    groupEvts,
    reserveEvts,
    eventTemplates,
    venueTemplates,
    contentTemplates,
    staffTemplates,
    eventTemplateTranslations,
    contentTemplateTranslations,
    venueTemplateTranslations,
    commonTextTranslations,
    dayBundle,
    onAddBtnClick,
    onUpdateContent,
    onEditContent,
    onRemoveContent,
    onCreateEvent,
    onUpdateEvent,
    onEditEvent,
    onRemoveEvent,
    onUpdateVenue,
    className: propClasses,
    style: propStyles,
    ...rest
  } = props;

  let classes = ['DailySchedulePanel border-l flex flex-col bg-white'];
  if (open) classes.push('open');
  if (propClasses) classes.push(propClasses);

  let styles: React.CSSProperties = { width };
  if (propStyles) styles = { ...styles, ...propStyles };

  const serverInfo = useServerInfo();
  const { user } = useUserInfo();
  const { activeProperty: property } = useActiveProperty();

  const [search, setSearch] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [mode, setMode] = useState<DailySchedulePanelMode>('default');

  const [focusLang, setFocusLang] = useState<string | undefined>(undefined);
  const [focusEdp, setFocusEdp] = useState<string>(serverInfo.mcmEndpoints[0]);
  const [focusZone, setFocusZone] = useState<string | undefined>(property.zones[0]?._id);

  const [activeTab, setActiveTab] = useState<SchedulePanelTabs>('events');
  const [mcmVisibleEdps, setMcmVisibleEdps] = useState<{ [key: string]: boolean }>(
    serverInfo.mcmEndpoints.reduce((acc, x, i) => {
      const result = { ...acc, [x]: i <= 2 }; // only select first 3 by default
      return result;
    }, {})
  );

  const togglePanelBtn = (
    <BubbleButton
      variant={open ? 'white' : 'crimson'}
      inverse={open}
      className="h-full py-0-i rounded-0-i grid items-center justify-center"
      onClick={() => onToggle(!open)}
      icon={open ? 'xmark-large' : 'list'}
    />
  );

  const userCanEdit = canEditCanvas(user);
  const dayIsLocked = isDayLocked(dayBundle?.day.locked ?? false);
  const { templateStatus } = extractTemplateStatus(dayBundle?.day);

  const eventsLocked = !userCanEdit || dayIsLocked || templateStatus.events === 'linked';
  const venueLocked = !userCanEdit || dayIsLocked || templateStatus.venueData === 'linked';
  const contentLocked = !userCanEdit || dayIsLocked || templateStatus.content === 'linked';

  useImperativeHandle(ref, () => ({
    toggle: tab => {
      setActiveTab(tab);
    },
  }));

  const hourFormat = getHourFormat(props.plan.property);

  return (
    <div {...rest} className={classes.join(' ')} style={styles}>
      <div className="DailySchedulePanelList flex-shrink-0 flex items-stretch bg-crimson">
        {!open && <>{togglePanelBtn}</>}
        <span className="mx-3 font-bold uppercase text-white self-center">DAILY SCHEDULE</span>
        <div className="ml-auto"></div>
        {togglePanelBtn}
      </div>
      <div className="flex items-center">
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'events' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('events')}
        >
          Events
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'venueData' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('venueData')}
        >
          Venues
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'content' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('content')}
        >
          Content
        </BubbleButton>
      </div>
      <div className="flex-grow overflow-y-auto">
        <div className="sticky top-0" style={{ zIndex: 12 }}>
          <div className="flex items-stretch bg-white" style={{ height: 37 }}>
            {activeTab !== 'venueData' && (
              <BubbleButton
                variant="lime"
                appearance="squared"
                icon="plus"
                disabled={
                  (activeTab === 'events' && eventsLocked) ||
                  (activeTab === 'content' && contentLocked)
                }
                onClick={() => onAddBtnClick(activeTab)}
              />
            )}
            <BubbleInput
              type="text"
              className="flex-grow"
              block
              appearance="bare"
              inputClassName="px-3 py-2 border-b"
              value={search}
              onValue={setSearch}
              placeholder="Search..."
            />
            <BubbleButton
              variant="ocean"
              appearance="squared"
              icon="filter"
              onClick={() => {
                setShowSettings(state => !state);
              }}
            />
          </div>
          {showSettings && (
            <div className="bg-eggshell border-b border-t p-3 pb-5 grid gap-4 shadow-lg">
              <BubbleFormGroup label="Mode" className="-mb-2">
                <BubbleCheckbox
                  inline
                  className="mr-3 mb-2"
                  onValue={() => setMode('default')}
                  checked={mode === 'default'}
                  appearance="rounded"
                  label="Default"
                />
                <BubbleCheckbox
                  inline
                  className="mr-3 mb-2"
                  onValue={() => setMode('mcm')}
                  checked={mode === 'mcm'}
                  appearance="rounded"
                  label="MCM"
                />
                <BubbleCheckbox
                  inline
                  className="mr-3 mb-2"
                  onValue={() => setMode('focus')}
                  checked={mode === 'focus'}
                  appearance="rounded"
                  label="Preview*"
                />
              </BubbleFormGroup>
              {mode === 'mcm' && (
                <BubbleFormGroup label="Endpoints" className="-mb-2">
                  {serverInfo.mcmEndpoints.map(edp => (
                    <div className="inline-flex items-center mr-3 mb-2 relative" key={edp}>
                      <BubbleInput
                        type="checkbox"
                        checked={mcmVisibleEdps[edp]}
                        inputClassName="stretched-link"
                        onValue={chk => setMcmVisibleEdps({ ...mcmVisibleEdps, [edp]: chk })}
                      />
                      <FontAwesomeIcon
                        className="ml-1"
                        fixedWidth
                        icon={mcmEndpointToIcon(edp, true)}
                      />
                      <span className="ml-1">{mcmEndpointToName(edp)}</span>
                    </div>
                  ))}
                </BubbleFormGroup>
              )}
              {mode === 'focus' && (
                <>
                  <BubbleFormGroup label="Endpoint" className="-mb-2">
                    {serverInfo.mcmEndpoints.map(edp => (
                      <div className="inline-flex items-center mr-3 mb-2 relative" key={edp}>
                        <BubbleInput
                          type="checkbox"
                          checked={focusEdp === edp}
                          onValue={() => setFocusEdp(edp)}
                          inputClassName="stretched-link"
                          appearance="rounded"
                        />
                        <FontAwesomeIcon
                          className="ml-1"
                          fixedWidth
                          icon={mcmEndpointToIcon(edp, true)}
                        />
                        <span className="ml-1">{mcmEndpointToName(edp)}</span>
                      </div>
                    ))}
                  </BubbleFormGroup>
                  {property.zones.length > 0 && (
                    <BubbleFormGroup label="Audience" className="-mb-2">
                      {property.zones.map(zone => (
                        <BubbleCheckbox
                          key={zone._id}
                          inline
                          className="mr-3 mb-2"
                          checked={focusZone === zone._id}
                          onValue={() => setFocusZone(zone._id)}
                          appearance="rounded"
                          label={zone.name}
                        />
                      ))}
                    </BubbleFormGroup>
                  )}
                  <BubbleFormGroup label="Language" className="-mb-2">
                    <BubbleCheckbox
                      inline
                      className="mr-3 mb-2"
                      key={serverInfo.defaultLanguage.key}
                      checked={focusLang === undefined}
                      onValue={() => setFocusLang(undefined)}
                      appearance="rounded"
                      label={serverInfo.defaultLanguage.englishLabel}
                    />
                    {serverInfo.languages.map(lang => (
                      <BubbleCheckbox
                        inline
                        className="mr-3 mb-2"
                        key={lang.key}
                        checked={focusLang === lang.key}
                        onValue={() => setFocusLang(lang.key)}
                        appearance="rounded"
                        label={lang.englishLabel}
                      />
                    ))}
                  </BubbleFormGroup>
                </>
              )}
              {mode === 'focus' && (
                <div className="text-xs text-slate italic">
                  *Preview mode applies merged variables, translations, and all other overrides as
                  seen by guests. Use this to get better insights to what will be hidden and why.
                </div>
              )}
            </div>
          )}
        </div>
        {activeTab === 'events' ? (
          <DailyEventListPanel
            mode={mode}
            width={width}
            locked={eventsLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            groupEvts={groupEvts}
            reserveEvts={reserveEvts}
            eventTemplates={eventTemplates}
            venueTemplates={venueTemplates}
            staffTemplates={staffTemplates}
            eventTemplateTranslations={eventTemplateTranslations}
            commonTextTranslations={commonTextTranslations}
            onCreateEvent={onCreateEvent}
            onUpdateEvent={onUpdateEvent}
            onDeleteEvent={onRemoveEvent}
            onEditEvent={onEditEvent}
            dayBundle={dayBundle}
          />
        ) : activeTab === 'content' ? (
          <DailyContentListPanel
            mode={mode}
            locked={contentLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            venueTemplates={venueTemplates}
            contentTemplates={contentTemplates}
            contentTemplateTranslations={contentTemplateTranslations}
            onDeleteContent={onRemoveContent}
            onUpdateContent={onUpdateContent}
            onEditContent={onEditContent}
            dayBundle={dayBundle}
          />
        ) : activeTab === 'venueData' ? (
          <DailyVenueListPanel
            mode={mode}
            locked={venueLocked}
            search={search}
            focusEdp={focusEdp}
            focusLang={focusLang}
            focusZone={focusZone}
            mcmVisibleEdps={serverInfo.mcmEndpoints.filter(edp => mcmVisibleEdps[edp])}
            plan={plan}
            hourFormat={hourFormat}
            venueTemplates={venueTemplates}
            venueTemplateTranslations={venueTemplateTranslations}
            commonTextTranslations={commonTextTranslations}
            onUpdateVenue={onUpdateVenue}
          />
        ) : (
          <>Can't resolve daily schedule list component</>
        )}
      </div>
    </div>
  );
});
